import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available color schemes
const COLOR_SCHEMES = {
  emerald: {
    primary: '160 84% 39%',
    primaryForeground: '0 0% 98%',
    accent: '160 84% 39%',
  },
  blue: {
    primary: '217 91% 60%',
    primaryForeground: '0 0% 98%',
    accent: '217 91% 60%',
  },
  purple: {
    primary: '262 83% 58%',
    primaryForeground: '0 0% 98%',
    accent: '262 83% 58%',
  },
  orange: {
    primary: '24 95% 53%',
    primaryForeground: '0 0% 98%',
    accent: '24 95% 53%',
  },
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'dark';
  });

  const [colorScheme, setColorScheme] = useState(() => {
    const saved = localStorage.getItem('colorScheme');
    return saved || 'emerald';
  });

  // Apply theme (dark/light)
  useEffect(() => {
    localStorage.setItem('theme', theme);
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      root.style.colorScheme = 'light';
    }
  }, [theme]);

  // Apply color scheme
  useEffect(() => {
    localStorage.setItem('colorScheme', colorScheme);
    const root = document.documentElement;
    
    // Remove all color scheme classes
    Object.keys(COLOR_SCHEMES).forEach(scheme => {
      root.classList.remove(`theme-${scheme}`);
    });
    
    // Add current color scheme class
    root.classList.add(`theme-${colorScheme}`);
    
    // Apply CSS variables for the color scheme
    const colors = COLOR_SCHEMES[colorScheme];
    if (colors) {
      root.style.setProperty('--primary', colors.primary);
      root.style.setProperty('--primary-foreground', colors.primaryForeground);
      root.style.setProperty('--accent', colors.accent);
    }
  }, [colorScheme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      toggleTheme,
      colorScheme,
      setColorScheme,
      availableColors: Object.keys(COLOR_SCHEMES)
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
